import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Loader2, Search, ShieldCheck, Trash2, Users, X } from 'lucide-react';
import type { WidgetDefinition } from '../types';
import type { WidgetVerbFlags } from '../../components/DashboardEngine/types';

/**
 * 🔴 El diálogo pide **solo lo que usa**: el id, el nombre para el encabezado y los verbos. No una
 * `WidgetDefinition` completa.
 *
 * No es cosmética de tipos: el contenedor de la tarjeta recibe del motor una vista más estrecha
 * (`WidgetDefinitionBase`), así que exigir la forma completa obligaría a un cast en el único sitio
 * desde donde se abre. Pedir lo mínimo deja que lo abran los dos —la tarjeta y el panel del
 * catálogo— sin mentir sobre el tipo.
 */
type WidgetAccessTarget = Pick<WidgetDefinition, 'widget_id' | 'nombre'> & {
  permissions?: WidgetVerbFlags;
  /**
   * 🔴 **F5 — de aquí sale la vertical, igual que en el Constructor.** No se pide la vertical: se
   * pide la categoría y se deriva, con el mismo `verticalDeLaCategoria` que usa el Builder. Así
   * «que las dos pantallas coincidan» deja de ser disciplina y pasa a ser una consecuencia.
   */
  categoria?: string;
  /**
   * 🔴 **F5.f — lo único que decide si «privado» se le reserva a alguien.** Un indicador con dueño
   * privado es un tablero personal, y ahí el administrador NO entra. Un **sembrado** privado no
   * tiene dueño al que referirse: lo ven todos los administradores de esa vertical.
   */
  propietario_id?: string | null;
};
import { useDashboardTranslation } from '../i18n';
import { verticalDeLaCategoria } from '../components/WidgetBuilder/categories';
import { toast } from '../../components/Toast';
import type {
  WidgetAccessAdapter,
  WidgetAccessPerson,
  WidgetGrantLevel,
  WidgetGrantRow,
  WidgetGrantSubjectType,
  WidgetPolicy,
  WidgetVisibilityState,
} from './types';

/**
 * 🔴 **El diálogo de acceso de un widget: quién lo ve, y qué exige para verse.**
 *
 * Es la mitad que faltaba del modelo de permisos por instancia. Hasta ahora el tablero solo tenía la
 * pantalla de REVISIÓN —ver lo repartido y revocarlo—, así que conceder acceso o marcar un widget
 * como sensible solo se podía hacer por API. Eso alcanza para validar el modelo; no para
 * entregárselo a nadie.
 *
 * 🔴 **Una sola pantalla con DOS BLOQUES, y son las dos preguntas del modelo** (F5.b, 2026-09-14):
 *
 * - **① ¿Quién entra?** — la visibilidad y los accesos, JUNTOS. *Un administrador la salta.*
 * - **② ¿Quién sale?** — la sensibilidad, sola. *NADIE la salta, ni el administrador.*
 *
 * <para>Antes eran dos pestañas partidas por CAPACIDAD (`can_share` / `can_edit`), y eso **partía la
 * pregunta ① por la mitad**: *restringir* vivía en una pestaña y *a quién se lo abro* en la otra,
 * siendo el mismo acto. Quien abría la de personas veía una lista de accesos sin poder ver si el
 * indicador estaba restringido siquiera — que es la única razón por la que esos accesos importan.</para>
 *
 * <para>⚠️ **Quitar las pestañas NO es quitar el gateo.** Las capacidades siguen mandando: quien tiene
 * `can_share` y no `can_edit` no ve el bloque ②, ni la parte de visibilidad del ①. Se oculta el
 * BLOQUE, que es lo que el contrato por capacidades ya hacía — la pestaña era solo el envoltorio.</para>
 *
 * <para>Y el **alcance de caché salió de esta ventana**: no es visibilidad, es de quién es el dato.</para>
 *
 * **El kit no habla HTTP:** todo pasa por el `WidgetAccessAdapter` que provee el host. Y todo lo que
 * el diálogo ofrece sale de las capacidades que ese adaptador declara, así que un host con un modelo
 * más pequeño obtiene un formulario más pequeño en vez de casillas que su backend va a rechazar. Ver
 * `access/types.ts`.
 */
export const WidgetAccessDialog: React.FC<{
  widget: WidgetAccessTarget;
  adapter: WidgetAccessAdapter;
  isOpen: boolean;
  onClose: () => void;
  /** Se llama tras cualquier cambio que pueda alterar el catálogo del usuario. */
  onChanged?: () => void;
}> = ({ widget, adapter, isOpen, onClose, onChanged }) => {
  const { t } = useDashboardTranslation();
  const caps = adapter.capabilities ?? {};

  // ── Qué se ofrece, y por qué cada cosa ──────────────────────────────────────────────────────
  //
  // ⚠️ Se decide con los verbos que el SERVIDOR resolvió para ESTE widget. `can_share` gobierna
  // repartir; `can_edit` gobierna la política. Y `can_publish` gobierna una sola opción dentro de
  // ella —abrir a un módulo—, que es exactamente cómo lo gatea el backend: la escritura pide la
  // capacidad de editar y solo la transición hacia «de módulo» pide además la de publicar.
  const puedeCompartir = widget.permissions?.can_share === true;
  const puedeEditarPolitica =
    widget.permissions?.can_edit === true && !!adapter.getPolicy && !!adapter.savePolicy;
  const puedePublicar = widget.permissions?.can_publish === true;

  const niveles = useMemo<WidgetGrantLevel[]>(() => caps.levels ?? ['viewer'], [caps.levels]);
  const sujetosDeclarados = useMemo<WidgetGrantSubjectType[]>(
    () => caps.subjects ?? ['user'],
    [caps.subjects],
  );

  /**
   * 🔴 **La vertical del indicador, derivada de su CATEGORÍA** — el mismo `verticalDeLaCategoria`
   * que usa el Constructor, no una copia de la regla.
   */
  const vertical = useMemo(
    () => verticalDeLaCategoria(caps.categories ?? [], widget.categoria),
    [caps.categories, widget.categoria],
  );

  /**
   * 🔴 **F5.a.2 — el sujeto «Un módulo» NO se recorta: se RETIRA** en todo indicador que declare
   * vertical.
   *
   * <para>Recortarlo a «solo la suya» parecía la respuesta, y es peor que no hacer nada: deja una
   * opción con **exactamente una respuesta posible**, y esa respuesta —conceder a su propia
   * vertical— **es lo mismo que no haberlo restringido**. O sea un formulario que ofrece deshacer lo
   * que acabas de hacer, sin decirlo. Las otras dos verticales, ofrecidas, producirían un acceso que
   * se guarda y **no abre nada**.</para>
   *
   * <para>🟢 Sigue existiendo en un indicador **sin vertical** —los personales creados antes de F1,
   * a los que el servidor vaciaba `roles_permitidos`—: ahí no hay techo, así que conceder a un
   * módulo sí abre algo. Es la única razón por la que el sujeto no desaparece del contrato.</para>
   */
  /**
   * 🔴 **Esta pantalla solo reparte a PERSONAS** (2026-09-14). Se retira «Un permiso», y «Un módulo»
   * ya se retiraba en cuanto el indicador declara vertical. Con una sola opción no hay selector: los
   * botones de sujeto no se pintan.
   *
   * ⚠️ **El contrato NO cambia**, y las ramas de `permission` y `module` de más abajo se quedan: el
   * host sigue declarando los tres sujetos, el servidor sigue aceptándolos y el panel de revisión
   * sigue mostrando los grants que ya existan. Lo que se retira es **ofrecerlos aquí**.
   *
   * 🔴 Y hay que decir lo que cuesta: conceder **por un permiso** es el mecanismo que la auditoría
   * llama central —el que pasó cinco revisiones funcionando en el servidor con su única pantalla
   * inutilizable— y vuelve a quedarse **solo-API**.
   */
  const sujetos = useMemo<WidgetGrantSubjectType[]>(
    () => sujetosDeclarados.filter((s) => s === 'user'),
    [sujetosDeclarados],
  );

  // ── Accesos concedidos ──────────────────────────────────────────────────────────────────────
  const [filas, setFilas] = useState<WidgetGrantRow[] | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const cargarFilas = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      setFilas(await adapter.listGrants(widget.widget_id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCargando(false);
    }
  }, [adapter, widget.widget_id]);

  useEffect(() => {
    if (isOpen && puedeCompartir) void cargarFilas();
  }, [isOpen, puedeCompartir, cargarFilas]);

  // ── El formulario de conceder ───────────────────────────────────────────────────────────────
  const [tipoSujeto, setTipoSujeto] = useState<WidgetGrantSubjectType>(sujetos[0]);
  const [sujetoId, setSujetoId] = useState('');
  const [sujetoLabel, setSujetoLabel] = useState('');
  const [nivel, setNivel] = useState<WidgetGrantLevel>(niveles[0]);
  const [vencimiento, setVencimiento] = useState<'default' | 'ninguno' | 'fecha'>('default');
  const [fecha, setFecha] = useState('');

  /**
   * 🔴 **El borrador.** `agregados` son accesos que solo existen en pantalla; `revocados`, ids que
   * se van al guardar. La lista que se pinta es `filas − revocados + agregados`, así que lo que se
   * ve es **el estado al que se va a llegar**, no una mezcla del actual con lo pedido.
   */
  const [agregados, setAgregados] = useState<WidgetGrantRow[]>([]);
  const [revocados, setRevocados] = useState<string[]>([]);

  /** La política tal y como vino del servidor, para saber si cambió. */
  const politicaOriginal = useRef<WidgetPolicy | null>(null);

  /**
   * El vencimiento que se aplica de verdad.
   *
   * 🔴 **Con «por defecto» se CALCULA la fecha aquí.** Antes se mandaba `undefined` y el acceso
   * nacía **sin vencimiento** — o sea, el selector decía «caduca a los 182 días» y la fila guardada
   * decía «nunca». Dos datos que se contradicen, y el que manda es el invisible.
   */
  const vencimientoElegido = (): string | null | undefined => {
    if (vencimiento === 'ninguno') return null;
    if (vencimiento === 'fecha') return fecha || undefined;
    const dias = caps.expiry?.defaultDays;
    if (!dias) return undefined;
    const d = new Date();
    d.setDate(d.getDate() + dias);
    return d.toISOString();
  };

  // ── El buscador de permisos (solo si el host los declara) ───────────────────────────────────
  //
  // A diferencia del de personas, este NO consulta al servidor: la lista viene entera en las
  // capacidades y se filtra en memoria. Y eso no es una simplificación, es lo correcto — la lista
  // que hay que ofrecer son LOS PERMISOS DE QUIEN CONCEDE, que el host ya conoce. El servidor exige
  // que uno tenga el permiso que asocia, así que ofrecer cualquier otro es ofrecer un 403.
  const permisosOfrecibles = useMemo(() => caps.permissions?.options ?? [], [caps.permissions]);

  // ── El buscador de personas (solo si el host lo provee) ─────────────────────────────────────
  const [consulta, setConsulta] = useState('');
  const [personas, setPersonas] = useState<WidgetAccessPerson[]>([]);
  const [buscando, setBuscando] = useState(false);

  // Con la caja vacía se ofrece la lista entera: son los permisos propios, no un catálogo ajeno, y
  // verlos completos es parte de saber qué se está eligiendo.
  const permisosFiltrados = useMemo(() => {
    const q = consulta.trim().toLowerCase();
    if (!q) return permisosOfrecibles;
    return permisosOfrecibles.filter(
      (pm) => pm.id.toLowerCase().includes(q) || pm.label.toLowerCase().includes(q),
    );
  }, [consulta, permisosOfrecibles]);

  /**
   * 🔴 **El efecto NO depende de `adapter`, y es una cicatriz.**
   *
   * <para>`adapter` es un objeto que construye el host. Basta con que su memo pierda una dependencia
   * —o no lo memoice— para que cambie de identidad en cada render: el efecto se remonta, el
   * `clearTimeout` del cleanup cancela el debounce **antes de lanzarlo**, y el buscador se queda
   * girando sin llegar a pedir nada. Pasó en el Constructor, con un `.bind()` en el JSX.</para>
   *
   * <para>Se lee por `ref`, que es justo lo que se quiere: usar siempre el último adaptador sin que
   * su identidad dispare nada. Y `setBuscando(true)` va DENTRO del `setTimeout`, para que un
   * debounce cancelado no deje el spinner encendido.</para>
   *
   * <para>El contador descarta respuestas fuera de orden: sin él, teclear rápido deja pintados los
   * resultados de una consulta anterior y se elegiría a alguien que no se buscó.</para>
   */
  const adapterRef = useRef(adapter);
  useEffect(() => { adapterRef.current = adapter; });
  const turnoBusqueda = useRef(0);

  useEffect(() => {
    const buscar = adapterRef.current.searchPeople;
    if (!buscar || tipoSujeto !== 'user' || consulta.trim().length < 2) {
      setPersonas([]);
      setBuscando(false);
      return;
    }
    const turno = ++turnoBusqueda.current;
    // Debounce: el directorio de personas es una consulta al Access Manager, no una lista local.
    const id = setTimeout(async () => {
      setBuscando(true);
      try {
        const r = await buscar.call(adapterRef.current, consulta.trim());
        if (turno === turnoBusqueda.current) setPersonas(r);
      } catch {
        if (turno === turnoBusqueda.current) setPersonas([]);
      } finally {
        if (turno === turnoBusqueda.current) setBuscando(false);
      }
    }, 300);
    return () => clearTimeout(id);
  }, [consulta, tipoSujeto]);

  /**
   * 🔴 **Añadir ya NO escribe: encola.** Nada de esta ventana se persiste hasta «Guardar cambios».
   *
   * <para>Antes, conceder y revocar llamaban al API en el acto mientras la visibilidad esperaba a un
   * botón. Eso dejaba la pantalla contando **dos verdades a la vez**: media ventana ya había pasado
   * y la otra media no, y quien cerraba sin guardar se llevaba los accesos escritos igual. Ahora la
   * ventana entera es un borrador, y el pie dice cuántos cambios hay sin guardar.</para>
   */
  const encolarAcceso = () => {
    if (!sujetoId.trim()) return;
    const id = sujetoId.trim();
    setError(null);
    setAviso(null);

    // Revocar y volver a añadir a la misma persona se anulan: es un no-cambio, y contarlo como dos
    // sería mentir en el contador del pie.
    setRevocados((r) => r.filter((x) => x !== id));
    setAgregados((a) =>
      a.some((x) => x.subjectId === id)
        ? a
        : [
            ...a,
            {
              subjectType: tipoSujeto,
              subjectId: id,
              subjectLabel: sujetoLabel || undefined,
              level: nivel,
              expiresAt: vencimientoElegido(),
            },
          ],
    );

    setSujetoId('');
    setSujetoLabel('');
    setConsulta('');
  };

  /** Quitar tampoco escribe: si estaba solo en el borrador se deshace, y si no se marca para borrar. */
  const quitarAcceso = (fila: WidgetGrantRow) => {
    setError(null);
    if (agregados.some((a) => a.subjectId === fila.subjectId)) {
      setAgregados((a) => a.filter((x) => x.subjectId !== fila.subjectId));
      return;
    }
    setRevocados((r) => (r.includes(fila.subjectId) ? r : [...r, fila.subjectId]));
  };

  // ── La política del widget ──────────────────────────────────────────────────────────────────
  const [politica, setPolitica] = useState<WidgetPolicy | null>(null);
  const [guardando, setGuardando] = useState(false);

  // Se carga al ABRIR. Antes esperaba a que alguien pulsara la pestaña de política; sin pestañas,
  // la visibilidad forma parte del bloque ① y tiene que estar ahí desde el primer render — si no, el
  // bloque abriría sin saber si el indicador está restringido, que es justo lo que da sentido a la
  // lista de accesos que hay debajo.
  useEffect(() => {
    if (!isOpen || !puedeEditarPolitica || !adapter.getPolicy || politica) return;
    let vivo = true;
    setCargando(true);
    adapter
      .getPolicy(widget.widget_id)
      .then((p) => { if (vivo) { setPolitica(p); politicaOriginal.current = p; } })
      .catch((e) => { if (vivo) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [isOpen, puedeEditarPolitica, adapter, widget.widget_id, politica]);

  /** Lo que se ve: lo del servidor, menos lo marcado para quitar, más lo añadido en pantalla. */
  const filasEfectivas: WidgetGrantRow[] = [
    ...(filas ?? []).filter((f) => !revocados.includes(f.subjectId)),
    ...agregados,
  ];

  const politicaCambio =
    !!politica && !!politicaOriginal.current
    && politica.visibility !== politicaOriginal.current.visibility;

  const cambios = agregados.length + revocados.length + (politicaCambio ? 1 : 0);

  /**
   * 🔴 **Guardar aplica el borrador ENTERO, y en este orden.**
   *
   * <para>La política primero: si alguien pasó de «lo ve toda la vertical» a «personas específicas»,
   * aplicar antes los accesos dejaría una ventana en la que el indicador sigue abierto y ya tiene
   * lista — el estado que parece cerrado y no lo está.</para>
   *
   * <para>⚠️ **No hay transacción**, igual que en el reparto al crear. Si algo falla a mitad, se
   * dice **qué quedó sin aplicar** y se recarga lo que sí: dejar el borrador como si nada hubiera
   * pasado sería pedirle a alguien que adivine qué repetir.</para>
   */
  const guardarCambios = async () => {
    // 🔴 SIN `if (!politica) return`. Lo tenia, y dejaba a quien solo REPARTE —`can_share` sin
    // `can_edit`, que es el caso de un analista— sin poder guardar nada: su ventana no carga
    // politica, asi que salia por aqui y sus accesos no se escribian jamas. El boton se veia
    // habilitado y no hacia nada.
    setGuardando(true);
    setError(null);
    setAviso(null);

    // 🔴 Los avisos se ACUMULAN. Antes cada `setAviso` pisaba al anterior, así que de dos accesos
    // que quedaban inertes solo se veía uno — y quien compartía se iba creyendo que el otro sí
    // había surtido efecto. Se juntan y se pintan todos, sin repetir el mismo texto dos veces.
    const avisos: string[] = [];
    const fallidos: string[] = [];
    try {
      if (politica && politicaCambio && adapter.savePolicy) {
        const r = await adapter.savePolicy(widget.widget_id, politica);
        if (r?.warning) avisos.push(r.warning);
        politicaOriginal.current = politica;
      }

      for (const a of agregados) {
        try {
          const r = await adapter.grant(widget.widget_id, {
            subjectType: a.subjectType,
            subjectId: a.subjectId,
            level: a.level,
            expiresAt: a.expiresAt,
          });
          // El aviso de I-2: se concedió, y a esa persona NO le va a aparecer porque le falta una
          // capacidad de más arriba. No es un error —la fila queda escrita y servirá el día que le
          // den el permiso— pero callarlo dejaría a quien comparte creyendo que compartió.
          const res = r ?? undefined;
          if (res?.warning) avisos.push(res.warning);
          else if (res?.inert) avisos.push(t('dashboard.access.grantInert'));
        } catch {
          fallidos.push(a.subjectLabel || a.subjectId);
        }
      }

      for (const id of revocados) {
        const fila = (filas ?? []).find((f) => f.subjectId === id);
        try {
          await adapter.revoke(widget.widget_id, fila?.subjectType ?? 'user', id);
        } catch {
          fallidos.push(fila?.subjectLabel || id);
        }
      }

      setAgregados([]);
      setRevocados([]);

      // ── 🔴 SI NO FALLÓ NADA: la ventana SE CIERRA y lo dice fuera ────────────────────────
      //
      // Antes se quedaba abierta, con el pie en «Sin cambios» y sin una sola señal de que el
      // guardado hubiera ocurrido. Y ese silencio se lee mal en las dos direcciones: quien pulsa
      // no sabe si pasó algo —así que vuelve a pulsar—, y quien no mira el pie se queda con la
      // ventana delante creyendo que todavía tiene trabajo pendiente.
      //
      // ⚠️ **Avisar DESPUÉS de cerrar obliga a sacar el mensaje de aquí**: dentro del diálogo se
      // iría con él. Por eso va al `toast` del kit —que es del kit, no del host, así que esto no
      // cuesta genericidad— y por eso los avisos del host viajan también ahí en vez de perderse.
      if (!fallidos.length) {
        onChanged?.();
        if (avisos.length) toast.warning([...new Set(avisos)].join(' '));
        else toast.success(t('dashboard.access.savedOk'));
        onClose();
        return;
      }

      // ── 🔴 SI ALGO FALLÓ: NO se cierra ──────────────────────────────────────────────────
      //
      // Cerrar aquí sería lo peor de las dos opciones: el mensaje diría qué quedó sin aplicar y la
      // ventana desaparecería antes de que se pueda hacer nada al respecto. Se queda abierta, con
      // la lista recargada —para que se vea el estado REAL— y el error nombrando a quién le falta.
      if (avisos.length) setAviso([...new Set(avisos)].join(' '));
      await cargarFilas();
      onChanged?.();
      setError(t('dashboard.access.saveFailedSome', { quienes: fallidos.join(', ') }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setGuardando(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const cerrarConEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', cerrarConEsc);
    return () => window.removeEventListener('keydown', cerrarConEsc);
  }, [isOpen, onClose]);

  /**
   * La visibilidad a la que se vuelve al marcar. Se recuerda en vez de asumir `restricted`, para que
   * abrir y volver a cerrar un tablero **personal** no lo convierta en otra cosa.
   *
   * 🔴 **Va ANTES del `return null`, y no es cosmético.** Estaba después: React cuenta los hooks por
   * render, así que un montaje permanente con `isOpen` alternando renderizaba N hooks cerrado y N+2
   * abierto — *«Rendered more hooks than during the previous render»*. No reventaba porque los dos
   * sitios que lo montan (`WidgetWrapper`, `WidgetManagerPanel`) lo montan condicionalmente con
   * `isOpen` fijo en `true`; pero el diálogo se **exporta público**, así que era una mina para
   * cualquier host que lo montara de otra forma.
   */
  const visibilidadCerrada = useRef<WidgetVisibilityState>('restricted');
  useEffect(() => {
    if (politica && politica.visibility !== 'module') visibilidadCerrada.current = politica.visibility;
  }, [politica]);

  if (!isOpen) return null;

  const etiquetaNivel = (n: WidgetGrantLevel) => t(`dashboard.access.level.${n}`);

  /**
   * «15 mar 2027». Con día, mes corto y año en vez de `toLocaleDateString()`, que en es-CO sale
   * `15/3/2027` — tres números que hay que descifrar en una fila que ya tiene otros dos.
   */
  const fechaCorta = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

  /**
   * 🔴 **F5.a.1/5.a.3 — la visibilidad «de módulo» nombra la vertical: «Todo Ventas».**
   *
   * Decir «todo el módulo» mientras el Constructor dice «Público en Ventas» son **dos vocabularios
   * para un solo concepto**, y quien opera las dos pantallas tiene que traducir de memoria. Sin
   * vertical derivable se conserva el texto genérico, que es la verdad disponible.
   */
  /**
   * 🔴 **El rótulo de cada visibilidad lo resuelve el HOST, y el kit cae a un texto NEUTRO.**
   *
   * <para>⚠️ Esto estuvo muerto: el diálogo rediseñado elegía el texto él mismo con
   * `widget.propietario_id`, aplicando una regla que es del host —*«privado SIN dueño lo ven los
   * administradores»*—. Un kit que lo consumen Comercial, Financiero y Nómina no puede saber eso.
   * Ahora se pregunta; si el host no contesta, el kit dice lo que **sí** puede afirmar.</para>
   */
  const delHost = (v: WidgetVisibilityState) =>
    adapter.describeVisibility?.({
      widgetId: widget.widget_id,
      visibility: v,
      tieneDueno: !!widget.propietario_id,
      vertical: vertical ? { id: vertical.role!, label: vertical.label } : undefined,
    });

  const etiquetaVisibilidad = (v: WidgetVisibilityState, porDefecto: string) =>
    delHost(v)?.label ?? porDefecto;

  const pistaVisibilidad = (v: WidgetVisibilityState, porDefecto: string) =>
    delHost(v)?.hint ?? porDefecto;

  /**
   * 🔴 **Sin respuesta del host, la nota NO se pinta.** Afirmar «hay alguien que lo ve siempre» es
   * una afirmación sobre el modelo de autorización del host, no sobre este formulario.
   */
  const notaSiempreVe = adapter.describeAlwaysSee?.({
    widgetId: widget.widget_id,
    vertical: vertical ? { id: vertical.role!, label: vertical.label } : undefined,
  });

  /**
   * 🔴 **Restringido = «no es de módulo».** Es el binario que la casilla expresa, y agrupa a
   * propósito `restricted` y `private`: desde esta pantalla la pregunta es una sola —¿lo ve toda la
   * vertical, o solo los de la lista?—, y un tablero personal responde lo mismo que uno restringido.
   */
  const restringido = (politica?.visibility ?? 'module') !== 'module';

  /**
   * 🔴 **F5.e — «Hoy lo ve: …».** La resuelve el HOST; el kit la pinta y no la interpreta. Sigue la
   * política EN CURSO del formulario, no la guardada: si alguien marca «sensible», la frase cambia
   * antes de guardar — que es donde sirve.
   */
  const hoyLoVe = adapter.describeAudience?.({
    widgetId: widget.widget_id,
    policy: politica,
    grants: filasEfectivas,
    vertical: vertical ? { id: vertical.role!, label: vertical.label } : undefined,
  });

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('dashboard.access.dialogTitle')}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Cabecera */}
        <div className="flex items-start justify-between gap-4 p-5 border-b border-gray-200">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900">{t('dashboard.access.dialogTitle')}</h2>
            <p className="text-sm text-gray-500 truncate" title={widget.nombre}>{widget.nombre}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
            aria-label={t('dashboard.access.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/*
          🔴 F5.c — «Hoy lo ve: …», SIEMPRE y arriba del todo.
          La combinación de tres cerraduras es difícil de deducir mirando los controles, y hay un
          estado que hoy no tiene ninguna señal en pantalla: restringido + sensible SIN accesos no lo
          ve nadie. Decir el resultado convierte el modelo en algo que se lee en vez de calcularse.
          El texto lo resuelve el host; aquí solo se pinta.
        */}
        {hoyLoVe && (
          <div className="mx-5 mt-4 flex items-start gap-2 rounded-lg bg-blue-50 px-3 py-2.5 text-sm text-blue-900">
            <Users className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <p>
              <span className="font-semibold">{t('dashboard.access.audienceToday')}</span>{' '}
              <span data-testid="access-audience">{hoyLoVe}</span>
            </p>
          </div>
        )}

        {/*
          🔴 El área de contenido NO hace scroll: lo hace **solo la lista de accesos**. Con todo
          desplazándose, el banner de «Hoy lo ven» se iba de la pantalla justo al cambiar lo que
          describe — y el pie con el contador, también.
        */}
        <div className="flex-1 overflow-hidden flex flex-col p-5 pt-4">
          {error && (
            <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-red-50 text-sm text-red-800" role="alert">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> <span>{error}</span>
            </div>
          )}
          {aviso && (
            <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-amber-50 text-sm text-amber-900" role="status">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> <span>{aviso}</span>
            </div>
          )}

          {/*
            ══ ① ¿QUIÉN ENTRA? ═══════════════════════════════════════════════════════════════════
            La visibilidad y los accesos, JUNTOS, porque son el mismo acto: restringir sin decir a
            quién se lo abres deja el indicador sin nadie dentro, y abrirlo a alguien sin haberlo
            restringido no cambia nada. Partidos en dos pestañas, cada mitad parecía completa.
          */}
          <section aria-labelledby="bloque-quien-entra" className="mb-6">
            {/*
              🔴 **Sin encabezado de bloque.** Lo llevaba —«Widget disponible para:»— y con una sola
              sección en la ventana era un título repetido: el de la ventana ya dice de qué va. Y la
              columna «Un administrador la salta» se fue con él: decía en abstracto lo que la nota de
              debajo de la lista dice en concreto, donde importa.
            */}

            {/*
              🔴 **«Quién ve este indicador» — una LISTA y una CASILLA** (2026-09-14).
              ────────────────────────────────────────────────────────────────────────────────────
              Se retiran de esta pantalla los tres radios de visibilidad y el sujeto «Un permiso».
              Lo que queda es **quién lo ve**, en una lista, y una casilla para cerrarlo o abrirlo.

              **Por qué una casilla y no radios:** quitados «Solo su dueño» y el resto, lo que sobra
              es **binario** —o lo ve toda la vertical, o solo los de la lista— y un binario se dice
              con una casilla. Además deja ① y ② leyéndose igual.

              ⚠️ **ESTO ES SOLO LA PANTALLA.** El modelo conserva las tres visibilidades y los tres
              sujetos, y el servidor los sigue aceptando. Lo que queda pendiente, y hay que tenerlo
              escrito donde se vea: **conceder NO restringe solo**. Añadir a alguien en un indicador
              de módulo guarda el acceso y no cambia a quién alcanza hasta que se marque la casilla —
              la instancia ya está satisfecha para toda la vertical. La frase «Hoy lo ve…» de arriba
              es hoy lo único que lo delata, y por eso no se toca.
            */}


            {/*
              🔴 **LA CASILLA MANDA, Y LAS DOS OPCIONES CUELGAN DE ELLA** (2026-09-14).
              ──────────────────────────────────────────────────────────────────────────────────────
              Las tres visibilidades del modelo quedan cubiertas sin nombrar ninguna:

                  desmarcada                      → de módulo  (lo ve toda la vertical)
                  marcada + «Solo administradores» → privado
                  marcada + «Por usuario»          → restringido + la lista

              **Por qué una casilla arriba y radios debajo, y no tres radios planos:** la primera
              pregunta —¿está restringido?— es binaria y gobierna a la segunda. Puestas al mismo
              nivel, «Todo Ventas» competía visualmente con «Solo administradores» como si fueran
              del mismo orden, y no lo son: una abre y las otras dos cierran de formas distintas.

              ⚠️ **Desmarcarla es PUBLICAR** —abrir el indicador a un colectivo— y por eso pide
              `can_publish`. Marcarla y elegir entre las dos pide poder editar la política.
            */}
            {puedeEditarPolitica && politica ? (
              <>
                <label
                  className={`flex items-start gap-2 text-sm ${
                    restringido && !puedePublicar ? 'cursor-not-allowed opacity-60' : 'cursor-pointer text-gray-800'
                  }`}
                  title={restringido && !puedePublicar ? t('dashboard.access.restrictedNeedsPublish') : undefined}
                >
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={restringido}
                    disabled={restringido && !puedePublicar}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setPolitica({ ...politica, visibility: visibilidadCerrada.current });
                      } else {
                        visibilidadCerrada.current =
                          politica.visibility === 'module' ? 'restricted' : politica.visibility;
                        setPolitica({ ...politica, visibility: 'module' });
                      }
                    }}
                  />
                  <span>
                    <span className="font-medium">{t('dashboard.access.restrictedCheck')}</span>
                    <span className="block text-xs text-gray-500">
                      {restringido
                        ? t('dashboard.access.restrictedCheckOn')
                        : pistaVisibilidad(
                            'module',
                            vertical
                              ? t('dashboard.access.restrictedCheckOffNamed', { alcance: vertical.label })
                              : t('dashboard.access.restrictedCheckOff'),
                          )}
                    </span>
                  </span>
                </label>

                {/*
                  Las dos formas de cerrarlo. Excluyentes de verdad —son dos visibilidades distintas
                  del modelo— así que radios, no casillas.

                  ⚠️ **«Solo administradores» es `private`, y en un indicador CON DUEÑO significa otra
                  cosa**: ahí el administrador no entra y quien queda es su dueño. Por eso el rótulo
                  cambia, en vez de mentir con el mismo texto para dos estados distintos.
                */}
                {restringido && (
                  <div className="ml-6 mt-3 space-y-3 border-l-2 border-gray-100 pl-4">
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name="alcance"
                        className="mt-1"
                        checked={politica.visibility === 'private'}
                        onChange={() => setPolitica({ ...politica, visibility: 'private' })}
                      />
                      <span>
                        <span className="font-medium text-gray-800">
                          {etiquetaVisibilidad('private', t('dashboard.access.scopeClosed'))}
                        </span>
                        <span className="block text-xs text-gray-500">
                          {pistaVisibilidad('private', t('dashboard.access.scopeClosedHint'))}
                        </span>
                      </span>
                    </label>

                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name="alcance"
                        className="mt-1"
                        checked={politica.visibility === 'restricted'}
                        onChange={() => setPolitica({ ...politica, visibility: 'restricted' })}
                      />
                      <span>
                        <span className="font-medium text-gray-800">
                          {etiquetaVisibilidad('restricted', t('dashboard.access.scopeUsers'))}
                        </span>
                        <span className="block text-xs text-gray-500">
                          {pistaVisibilidad('restricted', t('dashboard.access.scopeUsersHint'))}
                        </span>
                      </span>
                    </label>

                    {politica.visibility === 'restricted' && (
                      <div className="ml-6">
                    {/*
                      El reparto sigue gateado por `can_share`. Quitar los radios NO es quitar el gateo:
                      quien puede editar la política pero no repartir no debe ver este formulario — se lo
                      ofreceríamos para que el API se lo rechazara.
                    */}
                    {puedeCompartir && (
                      <>
                      <div className="space-y-3 mb-6">
                        {sujetos.length > 1 && (
                          <div className="flex gap-2">
                            {sujetos.map((s) => (
                              <button
                                key={s}
                                onClick={() => { setTipoSujeto(s); setSujetoId(''); setSujetoLabel(''); setConsulta(''); }}
                                className={`px-2.5 py-1 text-xs rounded-full border ${
                                  tipoSujeto === s
                                    ? 'border-blue-600 text-blue-700 bg-blue-50'
                                    : 'border-gray-200 text-gray-600'
                                }`}
                              >
                                {t(`dashboard.access.subject.${s}`)}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* El sujeto: buscado si el host tiene directorio, tecleado si no */}
                        {tipoSujeto === 'user' && adapter.searchPeople ? (
                          <div className="relative">
                            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                            <input
                              value={sujetoLabel || consulta}
                              onChange={(e) => { setConsulta(e.target.value); setSujetoLabel(''); setSujetoId(''); }}
                              placeholder={t('dashboard.access.searchPeople')}
                              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            {buscando && <Loader2 className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 animate-spin" />}
                            {personas.length > 0 && !sujetoId && (
                              <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                {personas.map((p) => (
                                  <li key={p.id}>
                                    <button
                                      onClick={() => { setSujetoId(p.id); setSujetoLabel(p.nombre || p.id); setPersonas([]); }}
                                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                                    >
                                      <span className="font-medium text-gray-800">{p.nombre || p.id}</span>
                                      {p.email && <span className="text-gray-500 ml-2">{p.email}</span>}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ) : tipoSujeto === 'module' && caps.modules?.length ? (
                          /*
                           * Solo se llega aquí en un indicador SIN vertical: con vertical, el sujeto se
                           * retiró de `sujetos`. Ver la nota de F5.a.2 arriba.
                           */
                          <select
                            value={sujetoId}
                            onChange={(e) => setSujetoId(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
                          >
                            <option value="">{t('dashboard.access.pickModule')}</option>
                            {caps.modules.map((m) => (
                              <option key={m.id} value={m.id}>{m.label}</option>
                            ))}
                          </select>
                        ) : tipoSujeto === 'permission' && permisosOfrecibles.length > 0 ? (
                          /*
                           * 🔴 El selector de permisos. Antes de esto, el mecanismo central —conceder a quien
                           * tenga un permiso— caia al `<input>` libre de abajo: habia que saberse el codigo
                           * entre cientos, y el premio por equivocarse era un 403 despues de teclear. Es la
                           * misma falla que ya se habia cerrado para las clases de sensibilidad.
                           *
                           * Es BUSCABLE y no un `<select>` porque la lista de un administrador es larga, y
                           * porque el codigo se reconoce leyendolo, no desplegando doscientas opciones.
                           */
                          <div className="relative">
                            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                            <input
                              value={sujetoLabel || consulta}
                              onChange={(e) => { setConsulta(e.target.value); setSujetoLabel(''); setSujetoId(''); }}
                              placeholder={t('dashboard.access.searchPermissions')}
                              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            {permisosFiltrados.length > 0 && !sujetoId && (
                              <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                {permisosFiltrados.map((pm) => (
                                  <li key={pm.id}>
                                    <button
                                      onClick={() => { setSujetoId(pm.id); setSujetoLabel(pm.label || pm.id); setConsulta(''); }}
                                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                                    >
                                      <span className="font-medium text-gray-800">{pm.label || pm.id}</span>
                                      {pm.label && pm.label !== pm.id && (
                                        <span className="text-gray-500 ml-2 text-xs font-mono">{pm.id}</span>
                                      )}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                            {consulta && permisosFiltrados.length === 0 && !sujetoId && (
                              <p className="mt-1 text-xs text-gray-500">
                                {t('dashboard.access.noPermissionMatch')}
                              </p>
                            )}
                            {/*
                              * La guarda del servidor, dicha donde se elige. «Solo puedes asociar un permiso
                              * que tú tienes» es una regla correcta que no estaba escrita en ninguna parte:
                              * ni en el modelo, ni en la doc, ni en la pantalla. Sin ella, una lista corta
                              * parece un catálogo incompleto en vez de la respuesta exacta.
                              */}
                            <p className="mt-1.5 text-xs text-gray-500">
                              {t('dashboard.access.permissionOwnScope')}
                            </p>
                          </div>
                        ) : (
                          <input
                            value={sujetoId}
                            onChange={(e) => setSujetoId(e.target.value)}
                            placeholder={t('dashboard.access.subjectIdPlaceholder')}
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
                          />
                        )}

                        {/*
                          * La advertencia del host para este tipo de sujeto. El kit la pinta y NO la
                          * interpreta: el texto viene ya resuelto, porque el kit no traduce mensajes de un
                          * modelo que no conoce. Es la misma regla que en WidgetGrantResult.warning.
                          */}
                        {caps.subjectHint?.[tipoSujeto] && (
                          <p className="flex items-start gap-2 text-xs text-amber-900 bg-amber-50 rounded-lg p-2.5">
                            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                            <span>{caps.subjectHint[tipoSujeto]}</span>
                          </p>
                        )}

                        <div className="flex flex-wrap gap-3 items-center">
                          {niveles.length > 1 && (
                            <select
                              value={nivel}
                              onChange={(e) => setNivel(e.target.value as WidgetGrantLevel)}
                              className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
                              aria-label={t('dashboard.access.level')}
                            >
                              {niveles.map((n) => (
                                <option key={n} value={n}>{etiquetaNivel(n)}</option>
                              ))}
                            </select>
                          )}

                          {caps.expiry && (
                            <>
                              <select
                                value={vencimiento}
                                onChange={(e) => setVencimiento(e.target.value as typeof vencimiento)}
                                className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
                                aria-label={t('dashboard.access.expiry')}
                              >
                                <option value="default">
                                  {caps.expiry.defaultDays
                                    ? t('dashboard.access.expiryDefaultDays').replace('{days}', String(caps.expiry.defaultDays))
                                    : t('dashboard.access.expiryDefault')}
                                </option>
                                <option value="fecha">{t('dashboard.access.expiryDate')}</option>
                                {caps.expiry.allowNoExpiry && (
                                  <option value="ninguno">{t('dashboard.access.expiryNever')}</option>
                                )}
                              </select>
                              {vencimiento === 'fecha' && (
                                <input
                                  type="date"
                                  value={fecha}
                                  onChange={(e) => setFecha(e.target.value)}
                                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
                                  aria-label={t('dashboard.access.expiryDate')}
                                />
                              )}
                            </>
                          )}

                          <button
                            onClick={encolarAcceso}
                            disabled={!sujetoId.trim()}
                            className="ml-auto px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40"
                          >
                            {t('dashboard.access.grant')}
                          </button>
                        </div>
                      </div>

                      {/* Lo ya concedido */}
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                        {t('dashboard.access.currentAccess')}
                      </h3>
                      {cargando && !filas ? (
                        <p className="text-sm text-gray-500 flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" /> {t('dashboard.access.loading')}
                        </p>
                      ) : !filasEfectivas.length ? (
                        <p className="text-sm text-gray-500">{t('dashboard.access.noneYet')}</p>
                      ) : (
                        <ul className="max-h-56 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-lg">
                          {filasEfectivas.map((f) => (
                            <li key={`${f.subjectType}:${f.subjectId}`} className="flex items-center gap-3 px-3 py-2">
                              <div className="min-w-0 flex-1">
                                <p className="text-sm text-gray-800 truncate">
                                  {f.subjectLabel || f.subjectId}
                                  {f.inert && (
                                    <span
                                      className="ml-2 text-[10px] uppercase font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded"
                                      title={t('dashboard.access.grantInert')}
                                    >
                                      {t('dashboard.access.inert')}
                                    </span>
                                  )}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {etiquetaNivel(f.level)}
                                  {' · '}
                                  {f.expiresAt
                                    ? t('dashboard.access.expiresOn').replace('{date}', fechaCorta(f.expiresAt))
                                    : t('dashboard.access.expiryNever')}
                                </p>
                              </div>
                              <button
                                onClick={() => quitarAcceso(f)}
                                className="p-1 text-gray-400 hover:text-red-600 rounded"
                                title={t('dashboard.access.revoke')}
                                aria-label={`${t('dashboard.access.revoke')} — ${f.subjectLabel || f.subjectId}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}

                      </>
                    )}

                {/*
                  🔴 **Una nota, no una fila del recuadro.** Estaba dentro de la lista de accesos, y
                  eso la hacía parecer **un acceso más**: algo que alguien concedió y que se puede
                  quitar. No lo es — quien entra por encima de la lista lo hace por una regla del
                  host, y no hay fila que borrar.

                  🔴 **Y la ESCRIBE EL HOST.** Era un literal del kit —«quienes administran X lo ven
                  siempre»—, o sea el kit afirmando que el modelo del host tiene un rol que salta la
                  lista. En Comercial es cierto; en un host sin ese bypass era una garantía falsa
                  debajo de la lista. Sin respuesta del host **no se pinta**: el kit no dice lo que no
                  puede saber, y menos sobre quién ve el dato a pesar de esta pantalla.
                */}
                {notaSiempreVe && (
                  <p className="mt-2 flex items-start gap-1.5 text-xs text-gray-500">
                    <ShieldCheck className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                    <span>{notaSiempreVe}</span>
                  </p>
                )}

                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              /* Sin poder editar la política no hay casilla ni radios: quien solo reparte ve la
                 lista, que es su única pantalla. El reparto sigue gateado por `can_share`: quitar
                 los radios NO es quitar el gateo. */
              puedeCompartir && (
              <>
              <div className="space-y-3 mb-6">
                {sujetos.length > 1 && (
                  <div className="flex gap-2">
                    {sujetos.map((s) => (
                      <button
                        key={s}
                        onClick={() => { setTipoSujeto(s); setSujetoId(''); setSujetoLabel(''); setConsulta(''); }}
                        className={`px-2.5 py-1 text-xs rounded-full border ${
                          tipoSujeto === s
                            ? 'border-blue-600 text-blue-700 bg-blue-50'
                            : 'border-gray-200 text-gray-600'
                        }`}
                      >
                        {t(`dashboard.access.subject.${s}`)}
                      </button>
                    ))}
                  </div>
                )}

                {/* El sujeto: buscado si el host tiene directorio, tecleado si no */}
                {tipoSujeto === 'user' && adapter.searchPeople ? (
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                    <input
                      value={sujetoLabel || consulta}
                      onChange={(e) => { setConsulta(e.target.value); setSujetoLabel(''); setSujetoId(''); }}
                      placeholder={t('dashboard.access.searchPeople')}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {buscando && <Loader2 className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 animate-spin" />}
                    {personas.length > 0 && !sujetoId && (
                      <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {personas.map((p) => (
                          <li key={p.id}>
                            <button
                              onClick={() => { setSujetoId(p.id); setSujetoLabel(p.nombre || p.id); setPersonas([]); }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                            >
                              <span className="font-medium text-gray-800">{p.nombre || p.id}</span>
                              {p.email && <span className="text-gray-500 ml-2">{p.email}</span>}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : tipoSujeto === 'module' && caps.modules?.length ? (
                  /*
                   * Solo se llega aquí en un indicador SIN vertical: con vertical, el sujeto se
                   * retiró de `sujetos`. Ver la nota de F5.a.2 arriba.
                   */
                  <select
                    value={sujetoId}
                    onChange={(e) => setSujetoId(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
                  >
                    <option value="">{t('dashboard.access.pickModule')}</option>
                    {caps.modules.map((m) => (
                      <option key={m.id} value={m.id}>{m.label}</option>
                    ))}
                  </select>
                ) : tipoSujeto === 'permission' && permisosOfrecibles.length > 0 ? (
                  /*
                   * 🔴 El selector de permisos. Antes de esto, el mecanismo central —conceder a quien
                   * tenga un permiso— caia al `<input>` libre de abajo: habia que saberse el codigo
                   * entre cientos, y el premio por equivocarse era un 403 despues de teclear. Es la
                   * misma falla que ya se habia cerrado para las clases de sensibilidad.
                   *
                   * Es BUSCABLE y no un `<select>` porque la lista de un administrador es larga, y
                   * porque el codigo se reconoce leyendolo, no desplegando doscientas opciones.
                   */
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                    <input
                      value={sujetoLabel || consulta}
                      onChange={(e) => { setConsulta(e.target.value); setSujetoLabel(''); setSujetoId(''); }}
                      placeholder={t('dashboard.access.searchPermissions')}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {permisosFiltrados.length > 0 && !sujetoId && (
                      <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {permisosFiltrados.map((pm) => (
                          <li key={pm.id}>
                            <button
                              onClick={() => { setSujetoId(pm.id); setSujetoLabel(pm.label || pm.id); setConsulta(''); }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                            >
                              <span className="font-medium text-gray-800">{pm.label || pm.id}</span>
                              {pm.label && pm.label !== pm.id && (
                                <span className="text-gray-500 ml-2 text-xs font-mono">{pm.id}</span>
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {consulta && permisosFiltrados.length === 0 && !sujetoId && (
                      <p className="mt-1 text-xs text-gray-500">
                        {t('dashboard.access.noPermissionMatch')}
                      </p>
                    )}
                    {/*
                      * La guarda del servidor, dicha donde se elige. «Solo puedes asociar un permiso
                      * que tú tienes» es una regla correcta que no estaba escrita en ninguna parte:
                      * ni en el modelo, ni en la doc, ni en la pantalla. Sin ella, una lista corta
                      * parece un catálogo incompleto en vez de la respuesta exacta.
                      */}
                    <p className="mt-1.5 text-xs text-gray-500">
                      {t('dashboard.access.permissionOwnScope')}
                    </p>
                  </div>
                ) : (
                  <input
                    value={sujetoId}
                    onChange={(e) => setSujetoId(e.target.value)}
                    placeholder={t('dashboard.access.subjectIdPlaceholder')}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
                  />
                )}

                {/*
                  * La advertencia del host para este tipo de sujeto. El kit la pinta y NO la
                  * interpreta: el texto viene ya resuelto, porque el kit no traduce mensajes de un
                  * modelo que no conoce. Es la misma regla que en WidgetGrantResult.warning.
                  */}
                {caps.subjectHint?.[tipoSujeto] && (
                  <p className="flex items-start gap-2 text-xs text-amber-900 bg-amber-50 rounded-lg p-2.5">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span>{caps.subjectHint[tipoSujeto]}</span>
                  </p>
                )}

                <div className="flex flex-wrap gap-3 items-center">
                  {niveles.length > 1 && (
                    <select
                      value={nivel}
                      onChange={(e) => setNivel(e.target.value as WidgetGrantLevel)}
                      className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
                      aria-label={t('dashboard.access.level')}
                    >
                      {niveles.map((n) => (
                        <option key={n} value={n}>{etiquetaNivel(n)}</option>
                      ))}
                    </select>
                  )}

                  {caps.expiry && (
                    <>
                      <select
                        value={vencimiento}
                        onChange={(e) => setVencimiento(e.target.value as typeof vencimiento)}
                        className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
                        aria-label={t('dashboard.access.expiry')}
                      >
                        <option value="default">
                          {caps.expiry.defaultDays
                            ? t('dashboard.access.expiryDefaultDays').replace('{days}', String(caps.expiry.defaultDays))
                            : t('dashboard.access.expiryDefault')}
                        </option>
                        <option value="fecha">{t('dashboard.access.expiryDate')}</option>
                        {caps.expiry.allowNoExpiry && (
                          <option value="ninguno">{t('dashboard.access.expiryNever')}</option>
                        )}
                      </select>
                      {vencimiento === 'fecha' && (
                        <input
                          type="date"
                          value={fecha}
                          onChange={(e) => setFecha(e.target.value)}
                          className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
                          aria-label={t('dashboard.access.expiryDate')}
                        />
                      )}
                    </>
                  )}

                  <button
                    onClick={encolarAcceso}
                    disabled={!sujetoId.trim()}
                    className="ml-auto px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40"
                  >
                    {t('dashboard.access.grant')}
                  </button>
                </div>
              </div>

              {/* Lo ya concedido */}
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                {t('dashboard.access.currentAccess')}
              </h3>
              {cargando && !filas ? (
                <p className="text-sm text-gray-500 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> {t('dashboard.access.loading')}
                </p>
              ) : !filasEfectivas.length ? (
                <p className="text-sm text-gray-500">{t('dashboard.access.noneYet')}</p>
              ) : (
                <ul className="max-h-56 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-lg">
                  {filasEfectivas.map((f) => (
                    <li key={`${f.subjectType}:${f.subjectId}`} className="flex items-center gap-3 px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-800 truncate">
                          {f.subjectLabel || f.subjectId}
                          {f.inert && (
                            <span
                              className="ml-2 text-[10px] uppercase font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded"
                              title={t('dashboard.access.grantInert')}
                            >
                              {t('dashboard.access.inert')}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">
                          {etiquetaNivel(f.level)}
                          {' · '}
                          {f.expiresAt
                            ? t('dashboard.access.expiresOn').replace('{date}', fechaCorta(f.expiresAt))
                            : t('dashboard.access.expiryNever')}
                        </p>
                      </div>
                      <button
                        onClick={() => quitarAcceso(f)}
                        className="p-1 text-gray-400 hover:text-red-600 rounded"
                        title={t('dashboard.access.revoke')}
                        aria-label={`${t('dashboard.access.revoke')} — ${f.subjectLabel || f.subjectId}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              </>
              )

            )}
          </section>

          {/*
            🔴 **UNA SOLA PREGUNTA, Y EL SEGUNDO EJE YA NO EXISTE.**
            ──────────────────────────────────────────────────────────────────────────────────────
            Aquí hubo un bloque ② para la **sensibilidad**: marcar que un indicador exige un permiso
            de dato. Se retiró de la pantalla el 2026-09-14 —dos preguntas que se parecen («las dos
            quitan gente») y se comportan al revés— y ese mismo día se retiró del modelo entero:
            columna, catálogo y permisos incluidos.

            🔴 **La consecuencia, escrita donde se pueda leer:** lo que queda decide **quién ve la
            tarjeta**, no quién puede leer la cifra. Los endpoints de datos siguen exigiendo solo el
            módulo (deuda B1), así que restringir aquí **oculta, no protege**.
          */}
        </div>

        {/*
          🔴 **El pie de la ventana, y el botón vive AQUÍ.**

          Estaba dentro del bloque de sensibilidad, que se retiró — y meterlo dentro del bloque de
          restringir habría repetido el mismo error con otro nombre: un botón que guarda **la
          ventana** no puede colgar de una de sus secciones, porque entonces parece que guarda solo
          esa. En el pie, fuera del área que hace scroll, está siempre a la vista y dice lo que hace.

          ⚠️ Solo se pinta si esta persona puede cambiar la política: quien únicamente reparte
          concede y revoca al instante, y no tiene nada que guardar.
        */}
        {/*
          🔴 **El contador a la izquierda, y no es adorno.** Con la ventana entera convertida en
          borrador, esta línea es lo único que separa «he cambiado tres cosas» de «no he tocado
          nada». Sin ella, cerrar sin guardar es indistinguible de guardar.
        */}
        <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-5 py-3">
          <span className={`text-xs ${cambios ? 'font-medium text-amber-700' : 'text-gray-400'}`}>
            {cambios
              ? t('dashboard.access.pendingChanges', { count: cambios })
              : t('dashboard.access.noChanges')}
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-3 py-2 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-50"
            >
              {t('dashboard.access.cancel')}
            </button>
            <button
              onClick={guardarCambios}
              disabled={guardando || !cambios}
              className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40"
            >
              {guardando ? t('dashboard.access.saving') : t('dashboard.access.saveChanges')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
